import React from 'react';
import {Skeleton} from '@mui/material';
import clsx from 'clsx';
import {WithClasses, WithOwnerState, styled} from '@netx/core-components/Theme';
import {composeClasses} from '@netx/core-components/Theme';
import {
	getListItemSkeletonUtilityClass,
	ListItemSkeletonClasses,
	LIST_ITEM_SKELETON_NAME,
} from './listItemSkeletonClasses';

type ListItemSkeletonOwnerState = WithClasses<
	ListItemSkeletonClasses,
	{
		hasSecondaryText?: boolean;
	}
>;

const useUtilityClasses = (state: ListItemSkeletonOwnerState) => {
	const {classes, hasSecondaryText} = state;
	const slots = {
		root: ['root', hasSecondaryText && 'hasSecondaryText'],
		startBox: ['startBox'],
		textBox: ['textBox'],
		buttonBox: ['buttonBox'],
	};
	return composeClasses(slots, getListItemSkeletonUtilityClass, classes);
};

const ListItemSkeletonRoot = styled('div', {
	name: LIST_ITEM_SKELETON_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<ListItemSkeletonOwnerState>) => ({
	width: '100%',
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	height: ownerState?.hasSecondaryText ? '76px' : '56px',
	padding: '0 16px',
}));

const ListItemSkeletonStartBox = styled('div', {
	name: LIST_ITEM_SKELETON_NAME,
	slot: 'StartBox',
})(({theme}) => ({
	width: '100%',
	height: '100%',
	display: 'flex',
	flex: '1 1 auto',
	overflow: 'hidden',
	alignItems: 'center',
}));

const ListItemSkeletonTextBox = styled('div', {
	name: LIST_ITEM_SKELETON_NAME,
	slot: 'TextBox',
})(({theme}) => ({
	width: '18%',
	marginLeft: '20px',
}));

const ListItemSkeletonButtonBox = styled('div', {
	name: LIST_ITEM_SKELETON_NAME,
	slot: 'ButtonBox',
})(({theme}) => ({
	width: '85px',
	height: '36px',
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	// the mui list secondary action is not subject the padding of the list item and is closer to the edge
	marginRight: theme.spacing(-2),
}));

export interface ListItemSkeletonProps
	extends React.HTMLAttributes<HTMLDivElement>,
		WithClasses<ListItemSkeletonClasses> {
	secondaryAction?: boolean;
	secondaryActionType?: 'circular' | 'rectangular';
	avatar?: boolean;
	secondaryText?: boolean;
}

export const ListItemSkeleton = ({
	classes: _classes,
	className,
	secondaryAction,
	secondaryActionType,
	avatar,
	secondaryText,
	...props
}: ListItemSkeletonProps) => {
	const ownerState: ListItemSkeletonOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);
	return (
		<ListItemSkeletonRoot className={clsx(classes.root, className)} {...props}>
			<ListItemSkeletonStartBox className={classes.startBox}>
				{avatar && <Skeleton variant="circular" height={40} width={40} />}
				<ListItemSkeletonTextBox className={classes.textBox}>
					<Skeleton variant="text" width="100%" />
					{secondaryText && <Skeleton variant="text" width="100%" />}
				</ListItemSkeletonTextBox>
			</ListItemSkeletonStartBox>
			<ListItemSkeletonButtonBox className={classes.buttonBox}>
				{secondaryAction && secondaryActionType === 'rectangular' && (
					<Skeleton height="100%" width="100%" variant="rectangular" />
				)}
				{secondaryAction && secondaryActionType === 'circular' && (
					<Skeleton height={42} width={42} variant="circular" />
				)}
			</ListItemSkeletonButtonBox>
		</ListItemSkeletonRoot>
	);
};

export default ListItemSkeleton;
